import { create } from 'zustand';
import Repository from '@/types/repositoryType';
import fetchOrganizationRepos from '@/apis/fetchOrganizationRepos';
import fetchUserRepos from '@/apis/fetchUserRepos';
import deleteRepoistory from '@/apis/deleteRepository';
import useSelectedStore from './selectedState';
import emptyRepository from '@/types/emptyRepository';
import RepositoryReqOrganization from '@/types/repositoryReqOrganizationType';
import RepositoryReqIndividual from '@/types/repositoryReqIndividualType';
import { addMergedRepo, addOrgRepo, addUserRepo } from '@/apis/ApiUtil';
import useUserStore from './userState';
import useOrganizationStore from './orgState';
import MergedRepositoryReq from '@/types/mergedRepoReqType';
import { logtail } from '@/utils/logtail';
import emptyHeaderObj from '@/types/emptyHeaderObj';
import headerObject from '@/types/headerObj';
import { getQueryClient } from './orgState';

const headerObj: headerObject = emptyHeaderObj;
headerObj.Useruuid = useUserStore.getState().user?.UserUUID;
headerObj.Useremail = useUserStore.getState().user?.Email;
headerObj.Username = useUserStore.getState().user?.FirstName;
headerObj.Orgname = useOrganizationStore.getState().organization?.OrgName;
headerObj.Orguuid = useOrganizationStore.getState().organization?.OrgUUID;

interface RepositoryState {
  organizationRepositoryList: Repository[];
  userRepositoryList: Repository[];
  setOrganizationRepositoryList: (uuid: string) => Promise<Repository[]>;
  setUserRepositoryList: (uuid: string) => Promise<Repository[]>;
  setUserRepositoryListByParam: (repo: Repository) => void;
  deleteFromRepositoryList: (repository: Repository) => Promise<boolean>;
  addToUserRepositoryList: (
    repositoryReq: RepositoryReqIndividual
  ) => Promise<string>;
  addToOrganizationRepositoryList: (
    repositoryReq: RepositoryReqOrganization
  ) => Promise<string>;
  addMergedToUserRepositoryList: (
    repositoryReq: MergedRepositoryReq
  ) => Promise<string>;
  addMergedToOrganizationRepositoryList: (
    repositoryReq: MergedRepositoryReq
  ) => Promise<string>;
  updatePrBot: (RepoUUID: string) => void;
  updateContiUpdates: (RepoUUID: string) => void;
  updateUserRepoList: () => Promise<number>;
  updateOrgRepoList: () => Promise<number>;
  clearRepositoryList: () => void;
}

const useRepositoryStore = create<RepositoryState>()((set, get) => ({
  organizationRepositoryList: [],
  userRepositoryList: [],

  setUserRepositoryListByParam: (repo: Repository) => {
    set({
      userRepositoryList: [repo],
    });

    // Invalidate relevant queries
    const queryClient = getQueryClient();
    if (repo.UserID) {
      queryClient.invalidateQueries({
        queryKey: ['repositories', 'user', repo.UserID],
      });
    }
  },

  setOrganizationRepositoryList: async (OrganizationUUID: string) => {
    if (!OrganizationUUID) {
      return [];
    }

    const queryClient = getQueryClient();

    // First try to get the data from the cache
    const cachedData = queryClient.getQueryData<Repository[]>([
      'repositories',
      'organization',
      OrganizationUUID,
    ]);
    if (cachedData) {
      set({ organizationRepositoryList: cachedData });
      return cachedData;
    }

    // If not in cache, fetch it and update the cache.
    //
    // No token is read or passed here. This used to poll
    // `user.accessToken` for 3s (10 x 300ms) and give up when it stayed empty,
    // which is now the NORMAL state: the credential moved to an httpOnly
    // cookie, `partialize` in UserContext deliberately drops accessToken from
    // localStorage, and requests reach the backend through the BFF
    // (`/api/be/*`) which attaches its own Bearer token server-side and
    // STRIPS any caller-supplied Authorization header. So the poll could only
    // succeed in the same page life as a fresh sign-in; after any reload it
    // burned 3s and then threw, leaving the org with an empty repository list.
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['repositories', 'organization', OrganizationUUID],
        queryFn: async () => fetchOrganizationRepos(OrganizationUUID),
      });

      set({ organizationRepositoryList: data });
      return data;
    } catch (error) {
      console.error('Error fetching organization repositories:', error);
      return [];
    }
  },

  setUserRepositoryList: async (UserUUID: string) => {
    if (!UserUUID) {
      logtail.error('UserUUID is empty', { UserUUID });
      logtail.flush();
      return [];
    }

    const queryClient = getQueryClient();

    // First try to get the data from the cache
    const cachedData = queryClient.getQueryData<Repository[]>([
      'repositories',
      'user',
      UserUUID,
    ]);

    if (cachedData) {
      set({ userRepositoryList: cachedData });
      return cachedData;
    }

    // If not in cache, fetch it and update the cache
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['repositories', 'user', UserUUID],
        queryFn: async () => {
          return await fetchUserRepos(UserUUID, headerObj);
        },
      });

      set({ userRepositoryList: data });
      return data;
    } catch (error) {
      console.error('Error fetching user repositories:', error);
      return [];
    }
  },

  deleteFromRepositoryList: async (repository: Repository) => {
    if (!repository || !repository.UUID) return true;
    const setSelectedRepository =
      useSelectedStore.getState().setSelectedRepository;

    const success = await deleteRepoistory(repository, headerObj);
    if (!success) return false;

    const queryClient = getQueryClient();

    if (!repository.OrganizationID) {
      // Invalidate user repositories query
      queryClient.invalidateQueries({
        queryKey: [
          'repositories',
          'user',
          useUserStore.getState().user?.UserUUID,
        ],
      });

      // Refetch data using our React Query wrapper
      const newUserRepoList = await get().setUserRepositoryList(
        useUserStore.getState().user?.UserUUID
      );

      await setSelectedRepository(emptyRepository, false);
    } else {
      // Invalidate organization repositories query
      queryClient.invalidateQueries({
        queryKey: ['repositories', 'organization', repository.OrganizationID],
      });

      // Refetch data using our React Query wrapper
      const newOrgList = await get().setOrganizationRepositoryList(
        useOrganizationStore.getState().organization?.OrgUUID
      );

      await setSelectedRepository(emptyRepository, false);
    }

    return true;
  },

  addToUserRepositoryList: async (repositoryReq: RepositoryReqIndividual) => {
    const user = useUserStore.getState().user;
    const setSelectedRepository =
      useSelectedStore.getState().setSelectedRepository;

    const res = await addUserRepo(repositoryReq, headerObj);
    if (res.Error !== 'None' && res.Error !== null && res.Error !== 'Time out')
      return res.Error;

    // Invalidate user repositories query
    const queryClient = getQueryClient();
    queryClient.invalidateQueries({
      queryKey: ['repositories', 'user', user?.UserUUID],
    });

    // Fetch updated data
    const newUserRepoList = await get().setUserRepositoryList(user?.UserUUID);

    return res.Error === 'Time out' ? 'Time out' : '';
  },

  addToOrganizationRepositoryList: async (
    repositoryReq: RepositoryReqOrganization
  ) => {
    const organization = useOrganizationStore.getState().organization;
    const setSelectedRepository =
      useSelectedStore.getState().setSelectedRepository;

    const res = await addOrgRepo(repositoryReq, headerObj);
    if (res.Error !== 'None' && res.Error !== null && res.Error !== 'Time out')
      return res.Error;

    // Invalidate organization repositories query
    const queryClient = getQueryClient();
    queryClient.invalidateQueries({
      queryKey: ['repositories', 'organization', organization?.OrgUUID],
    });

    // Fetch updated data
    const newOrgRepoList = await get().setOrganizationRepositoryList(
      organization?.OrgUUID
    );

    return res.Error === 'Time out' ? 'Time out' : '';
  },

  updateUserRepoList: async () => {
    const user = useUserStore.getState().user;

    // Invalidate and refetch user repositories
    const queryClient = getQueryClient();
    queryClient.invalidateQueries({
      queryKey: ['repositories', 'user', user?.UserUUID],
    });

    const newUserRepoList = await get().setUserRepositoryList(user?.UserUUID);
    return newUserRepoList.length;
  },

  updateOrgRepoList: async () => {
    const organization = useOrganizationStore.getState().organization;

    // Invalidate and refetch organization repositories
    const queryClient = getQueryClient();
    queryClient.invalidateQueries({
      queryKey: ['repositories', 'organization', organization?.OrgUUID],
    });

    const newOrgRepoList = await get().setOrganizationRepositoryList(
      organization?.OrgUUID
    );
    return newOrgRepoList.length;
  },

  addMergedToUserRepositoryList: async (repositoryReq: MergedRepositoryReq) => {
    const user = useUserStore.getState().user;
    const setSelectedRepository =
      useSelectedStore.getState().setSelectedRepository;

    const res = await addMergedRepo(repositoryReq, headerObj);
    if (res.Error !== null && res.Error !== 'None' && res.Error !== 'Time out')
      return res.Error;

    // Invalidate user repositories query
    const queryClient = getQueryClient();
    queryClient.invalidateQueries({
      queryKey: ['repositories', 'user', user?.UserUUID],
    });

    // Fetch updated data
    const newUserRepoList = await get().setUserRepositoryList(user?.UserUUID);

    return res.Error === 'Time out' ? 'Time out' : '';
  },

  addMergedToOrganizationRepositoryList: async (
    repositoryReq: MergedRepositoryReq
  ) => {
    const organization = useOrganizationStore.getState().organization;
    const setSelectedRepository =
      useSelectedStore.getState().setSelectedRepository;

    const res = await addMergedRepo(repositoryReq, headerObj);
    if (res.Error !== 'None' && res.Error !== null && res.Error !== 'Time out')
      return res.Error;

    // Invalidate organization repositories query
    const queryClient = getQueryClient();
    queryClient.invalidateQueries({
      queryKey: ['repositories', 'organization', organization?.OrgUUID],
    });

    // Fetch updated data
    const newOrgRepoList = await get().setOrganizationRepositoryList(
      organization?.OrgUUID
    );

    return res.Error === 'Time out' ? 'Time out' : '';
  },

  updatePrBot: (RepoUUID: string) => {
    const userRepositoryList = get().userRepositoryList;
    const orgRepositoryList = get().organizationRepositoryList;

    // Update userRepositoryList
    const newRepoList = userRepositoryList.map((repo) => {
      if (repo.UUID === RepoUUID) {
        repo.PRBotEnabled = true;
      }
      return repo;
    });

    set({ userRepositoryList: newRepoList });

    // Update organizationRepositoryList
    const newOrgRepoList = orgRepositoryList.map((repo) => {
      if (repo.UUID === RepoUUID) {
        repo.PRBotEnabled = true;
      }
      return repo;
    });

    set({ organizationRepositoryList: newOrgRepoList });

    // Invalidate relevant queries
    const queryClient = getQueryClient();
    queryClient.invalidateQueries({ queryKey: ['repositories'] });
  },

  updateContiUpdates: (RepoUUID: string) => {
    const userRepositoryList = get().userRepositoryList;
    const orgRepositoryList = get().organizationRepositoryList;

    // Update userRepositoryList
    const newRepoList = userRepositoryList.map((repo) => {
      if (repo.UUID === RepoUUID) {
        repo.ContinuousUpdatesEnabled = true;
      }
      return repo;
    });

    set({ userRepositoryList: newRepoList });

    // Update organizationRepositoryList
    const newOrgRepoList = orgRepositoryList.map((repo) => {
      if (repo.UUID === RepoUUID) {
        repo.ContinuousUpdatesEnabled = true;
      }
      return repo;
    });

    set({ organizationRepositoryList: newOrgRepoList });

    // Invalidate relevant queries
    const queryClient = getQueryClient();
    queryClient.invalidateQueries({ queryKey: ['repositories'] });
  },

  clearRepositoryList: () => {
    set({
      organizationRepositoryList: [],
      userRepositoryList: [],
    });
  },
}));

export default useRepositoryStore;
